import React, { useState } from 'react';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Avatar,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Divider
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  ShoppingCart,
  AttachMoney,
  People,
  Inventory,
  Star,
  Visibility
} from '@mui/icons-material';
import { Line, Bar, Doughnut, Pie } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const Analytics = () => {
  const [timeRange, setTimeRange] = useState('30');

  const salesData = {
    labels: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    datasets: [
      {
        label: 'Sales',
        data: [12000, 19000, 15000, 25000, 22000, 30000, 28000, 32000, 35000, 40000, 38000, 45000],
        borderColor: '#DC143C',
        backgroundColor: 'rgba(220, 20, 60, 0.1)',
        tension: 0.4,
        fill: true
      },
      {
        label: 'Revenue',
        data: [10000, 16000, 12000, 20000, 18000, 25000, 23000, 27000, 30000, 35000, 33000, 40000],
        borderColor: '#4CAF50',
        backgroundColor: 'rgba(76, 175, 80, 0.1)',
        tension: 0.4,
        fill: true
      }
    ]
  };

  const categoryData = {
    labels: ['Electronics', 'Computers', 'Audio', 'Tablets', 'Footwear', 'Clothing'],
    datasets: [
      {
        data: [35, 25, 15, 10, 10, 5],
        backgroundColor: [
          '#DC143C',
          '#FF6B6B',
          '#FF8E8E',
          '#FFB3B3',
          '#FFD6D6',
          '#FFE6E6'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const topProductsData = {
    labels: ['iPhone 15 Pro', 'MacBook Pro', 'Samsung Galaxy', 'iPad Pro', 'Sony Headphones'],
    datasets: [
      {
        label: 'Sales',
        data: [120, 85, 95, 70, 110],
        backgroundColor: [
          '#DC143C',
          '#FF6B6B',
          '#FF8E8E',
          '#FFB3B3',
          '#FFD6D6'
        ]
      }
    ]
  };

  const customerData = {
    labels: ['New', 'Returning', 'VIP', 'Inactive'],
    datasets: [
      {
        data: [40, 35, 15, 10],
        backgroundColor: [
          '#4CAF50',
          '#2196F3',
          '#FFC107',
          '#9E9E9E'
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }
    ]
  };

  const metricsCards = [
    {
      title: 'Total Sales',
      value: '$456,789',
      change: '+12.5%',
      trend: 'up',
      icon: <AttachMoney />,
      color: '#4CAF50'
    },
    {
      title: 'Total Orders',
      value: '1,234',
      change: '+8.2%',
      trend: 'up',
      icon: <ShoppingCart />,
      color: '#2196F3'
    },
    {
      title: 'Active Customers',
      value: '8,567',
      change: '+15.3%',
      trend: 'up',
      icon: <People />,
      color: '#FF9800'
    },
    {
      title: 'Average Rating',
      value: '4.8',
      change: '+0.2',
      trend: 'up',
      icon: <Star />,
      color: '#FFC107'
    }
  ];

  const topProducts = [
    { name: 'iPhone 15 Pro Max', sales: 120, revenue: 144000, growth: '+15%' },
    { name: 'MacBook Pro 16-inch', sales: 85, revenue: 297500, growth: '+12%' },
    { name: 'Samsung Galaxy S24', sales: 95, revenue: 123500, growth: '+8%' },
    { name: 'iPad Pro 12.9-inch', sales: 70, revenue: 77000, growth: '+20%' },
    { name: 'Sony WH-1000XM5', sales: 110, revenue: 44000, growth: '+25%' }
  ];

  const recentActivity = [
    { action: 'New order received', details: 'Order #ORD-001 for iPhone 15 Pro', time: '2 min ago', type: 'order' },
    { action: 'Payment processed', details: 'Payment of $1,199.99 received', time: '5 min ago', type: 'payment' },
    { action: 'Low stock alert', details: 'MacBook Pro running low (5 units)', time: '15 min ago', type: 'alert' },
    { action: 'Customer review', details: '5-star review for Samsung Galaxy', time: '1 hour ago', type: 'review' },
    { action: 'Inventory updated', details: 'Added 50 units of iPhone 15 Pro', time: '2 hours ago', type: 'inventory' }
  ];

  const getActivityIcon = (type) => {
    switch (type) {
      case 'order': return <ShoppingCart sx={{ color: '#DC143C' }} />;
      case 'payment': return <AttachMoney sx={{ color: '#4CAF50' }} />;
      case 'alert': return <Inventory sx={{ color: '#FF9800' }} />;
      case 'review': return <Star sx={{ color: '#FFC107' }} />;
      case 'inventory': return <Inventory sx={{ color: '#2196F3' }} />;
      default: return <Visibility sx={{ color: '#9E9E9E' }} />;
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#DC143C' }}>
          Analytics Dashboard
        </Typography>
        <FormControl sx={{ minWidth: 120 }}>
          <InputLabel>Time Range</InputLabel>
          <Select
            value={timeRange}
            onChange={(e) => setTimeRange(e.target.value)}
            label="Time Range"
          >
            <MenuItem value="7">Last 7 days</MenuItem>
            <MenuItem value="30">Last 30 days</MenuItem>
            <MenuItem value="90">Last 3 months</MenuItem>
            <MenuItem value="365">Last year</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Metrics Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {metricsCards.map((card, index) => (
          <Grid item xs={12} sm={6} md={3} key={index}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {card.title}
                    </Typography>
                    <Typography variant="h4" sx={{ fontWeight: 'bold', color: card.color, mb: 1 }}>
                      {card.value}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {card.trend === 'up' ? (
                        <TrendingUp sx={{ color: '#4CAF50', fontSize: 16 }} />
                      ) : (
                        <TrendingDown sx={{ color: '#F44336', fontSize: 16 }} />
                      )}
                      <Chip
                        label={card.change}
                        size="small"
                        sx={{
                          backgroundColor: card.trend === 'up' ? '#4CAF50' : '#F44336',
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                    </Box>
                  </Box>
                  <Avatar sx={{ bgcolor: card.color, width: 56, height: 56 }}>
                    {card.icon}
                  </Avatar>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>

      {/* Charts Row 1 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={8}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Sales & Revenue Trend
              </Typography>
              <Box sx={{ height: 400 }}>
                <Line 
                  data={salesData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'top'
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0,0,0,0.1)'
                        }
                      },
                      x: {
                        grid: {
                          color: 'rgba(0,0,0,0.1)'
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Sales by Category
              </Typography>
              <Box sx={{ height: 400 }}>
                <Doughnut 
                  data={categoryData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Charts Row 2 */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Top Selling Products
              </Typography>
              <Box sx={{ height: 300 }}>
                <Bar 
                  data={topProductsData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        display: false
                      }
                    },
                    scales: {
                      y: {
                        beginAtZero: true,
                        grid: {
                          color: 'rgba(0,0,0,0.1)'
                        }
                      },
                      x: {
                        grid: {
                          display: false
                        }
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold' }}>
                Customer Distribution
              </Typography>
              <Box sx={{ height: 300 }}>
                <Pie 
                  data={customerData}
                  options={{
                    responsive: true,
                    maintainAspectRatio: false,
                    plugins: {
                      legend: {
                        position: 'bottom'
                      }
                    }
                  }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Bottom Row */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Top Performing Products
              </Typography>
              <List>
                {topProducts.map((product, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: '#DC143C' }}>
                          {index + 1}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={product.name}
                        secondary={`${product.sales} units sold • $${product.revenue.toLocaleString()} revenue`}
                      />
                      <Chip
                        label={product.growth}
                        size="small"
                        sx={{
                          backgroundColor: '#4CAF50',
                          color: 'white',
                          fontSize: '0.7rem'
                        }}
                      />
                    </ListItem>
                    {index < topProducts.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} lg={6}>
          <Card>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 3, fontWeight: 'bold' }}>
                Recent Activity
              </Typography>
              <List>
                {recentActivity.map((activity, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'transparent' }}>
                          {getActivityIcon(activity.type)}
                        </Avatar>
                      </ListItemAvatar>
                      <ListItemText
                        primary={activity.action}
                        secondary={
                          <Box>
                            <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                              {activity.details}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                              {activity.time}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItem>
                    {index < recentActivity.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Analytics; 